import { useState } from "react";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/apiErrors";
import { Copy, ShieldCheck, Trash2 } from "lucide-react";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { Button, ButtonAdd, ButtonEdit } from "@/components/ui/button";
import Spinner from "@/components/Spinner";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { actionLabel, domainLabel, summarizePermissions } from "@/lib/permissions";
import AccessRoleFormModal from "../modals/AccessRoleFormModal";
import {
  useCreateAccessRoleMutation,
  useDeleteAccessRoleMutation,
  useGetAccessRolesQuery,
  useGetSystemPresetsQuery,
} from "@/redux/services/permission/accessRole.api";
import {
  AccessRole,
  AccessRoleScope,
} from "@/redux/services/permission/permission.type";

// Renders a role's permissions as one chip per domain·action pair.
const PermissionChips = ({ permissions }: { permissions: string[] }) => (
  <div className="mt-3 flex flex-wrap gap-1.5">
    {summarizePermissions(permissions).flatMap(({ domain, actions }) =>
      actions.map((action) => (
        <span
          key={`${domain}.${action}`}
          className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
        >
          {domainLabel(domain)} · {actionLabel(action)}
        </span>
      ))
    )}
  </div>
);

const ListeAccessRoles = () => {
  const { currentUser, currentRestaurant, currentFranchise } = useAppSelector(
    (s) => s.user
  );
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const confirm = useConfirm();

  const scope: AccessRoleScope | null = isOwner
    ? currentFranchise?.id
      ? { franchiseId: currentFranchise.id }
      : null
    : currentRestaurant?.id
      ? { restaurantId: currentRestaurant.id }
      : null;

  const { data: roles, isLoading } = useGetAccessRolesQuery(
    scope as AccessRoleScope,
    { skip: !scope }
  );
  const { data: presets, isLoading: presetsLoading } =
    useGetSystemPresetsQuery();
  const [deleteAccessRole] = useDeleteAccessRoleMutation();
  const [createAccessRole] = useCreateAccessRoleMutation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<AccessRole | undefined>();
  const [cloningId, setCloningId] = useState<string | null>(null);

  // A role is editable in this scope only if it is local to it. A restaurant
  // inherits its franchise's roles, which are read-only here.
  const isLocal = (role: AccessRole) =>
    isOwner
      ? !!role.franchise
      : !!role.restaurant && role.restaurant.id === currentRestaurant?.id;

  const openCreate = () => {
    setEditing(undefined);
    setIsFormOpen(true);
  };
  const openEdit = (role: AccessRole) => {
    setEditing(role);
    setIsFormOpen(true);
  };

  const handleDelete = async (role: AccessRole) => {
    if (!scope) return;
    const ok = await confirm({
      title: `Supprimer le rôle « ${role.name} » ?`,
      description:
        "Un rôle encore assigné à des employés ne peut pas être supprimé.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteAccessRole({ scope, id: role.id }).unwrap();
      toast.success("Rôle supprimé.");
    } catch (error) {
      // The API refuses (422 …error.in_use) while the role is assigned.
      toastApiError(error, "Impossible de supprimer le rôle.");
    }
  };

  // Clones a system preset into the current scope as a local, editable role.
  const handleClone = async (preset: AccessRole) => {
    if (!scope) return;
    setCloningId(preset.id);
    try {
      await createAccessRole({
        scope,
        data: {
          name: preset.name,
          description: preset.description ?? undefined,
          permissions: preset.permissions,
        },
      }).unwrap();
      toast.success(`Modèle « ${preset.name} » copié dans vos rôles.`);
    } catch {
      toast.error("Impossible de copier ce modèle.");
    } finally {
      setCloningId(null);
    }
  };

  if (!scope) {
    return (
      <p className="text-sm text-muted-foreground">
        Sélectionnez un restaurant pour gérer les rôles d'accès.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">
            Rôles & permissions
          </h3>
          <p className="text-sm text-muted-foreground">
            Définissez des rôles d'accès et assignez-les à vos employés pour
            accorder des permissions précises.
          </p>
        </div>
        <ButtonAdd label="Ajouter un rôle" type="button" onClick={openCreate} />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : !roles?.length ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
          <ShieldCheck size={40} strokeWidth={1.5} />
          <p>Aucun rôle d'accès pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {roles.map((role) => {
            const local = isLocal(role);
            return (
              <div
                key={role.id}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-card-foreground truncate">
                        {role.name}
                      </p>
                      {!local && (
                        <span className="flex-shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          Hérité
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {role.description}
                      </p>
                    )}
                  </div>
                  {local && (
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <ButtonEdit onClick={() => openEdit(role)} />
                      <button
                        type="button"
                        onClick={() => handleDelete(role)}
                        aria-label="Supprimer le rôle"
                        className="rounded-lg p-2 text-destructive transition-colors hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                <PermissionChips permissions={role.permissions} />
              </div>
            );
          })}
        </div>
      )}

      {/* System presets — the shared library. Clone one into this scope to get
          a local, editable role. */}
      <div className="space-y-3 pt-2">
        <div>
          <h4 className="text-base font-semibold text-card-foreground">
            Modèles système
          </h4>
          <p className="text-sm text-muted-foreground">
            Des rôles prédéfinis prêts à l'emploi. Copiez-en un pour l'adapter à
            votre établissement.
          </p>
        </div>

        {presetsLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : !presets?.length ? (
          <p className="text-sm text-muted-foreground">
            Aucun modèle système disponible.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {presets.map((preset) => (
              <div
                key={preset.id}
                className="rounded-xl border border-dashed border-border bg-muted/30 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-card-foreground truncate">
                      {preset.name}
                    </p>
                    {preset.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {preset.description}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0 gap-1.5"
                    isLoading={cloningId === preset.id}
                    onClick={() => handleClone(preset)}
                  >
                    <Copy className="h-4 w-4" />
                    Copier
                  </Button>
                </div>
                <PermissionChips permissions={preset.permissions} />
              </div>
            ))}
          </div>
        )}
      </div>

      {isFormOpen && (
        <AccessRoleFormModal
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          scope={scope}
          role={editing}
        />
      )}
    </div>
  );
};

export default ListeAccessRoles;
